/**
 * Parts Finder (Storefront API).
 *
 * Cascading Manufacturer > Series > Model > Engine > Year > Serial Number. Scales to the
 * catalog's size (57k+ products) without a client-side fetch-all: each step only ever reads
 * products matching the filters already chosen — narrowing down from the whole catalog only
 * once, for Manufacturer (see below), and to a few hundred/thousand products at most for every
 * field after that.
 *
 * "Filters" vs "Filter Kits" tabs use Shopify's native Product Type field (see kindOf()) —
 * not the custom.kit_type metafield, whose values describe kit contents ("Air/Oil/Fuel")
 * rather than a consistent kit/not-kit flag.
 *
 * Every field is a Combobox (see the class below) — a searchable dropdown with a text input
 * built into the open panel, not a native <select> or a plain <input list="..."> (both were
 * tried and replaced; neither renders a search box inside the open dropdown the way a real
 * combobox does, which is what was actually asked for). It's a from-scratch component rather
 * than the theme's `.select-custom` pattern (invisible absolutely-positioned select + a
 * separate label) — that pattern was fragile for this layout in earlier iterations of this
 * section (overlap/click-blocking/duplicate-label bugs) and doesn't support search at all
 * regardless.
 *
 * Every query in this file uses the Storefront API's `search(...)` root field, not
 * `collection(handle: ...).products(...)`. Two independent, confirmed reasons:
 *
 * 1. `Filter.values` aggregation is hard-capped at exactly 100 entries per filter, counted
 *    across the *entire catalog* regardless of which filters are already applied (e.g.
 *    querying Series with Manufacturer=Caterpillar already selected still returned the 100
 *    most-global Series values with everything else at count 0 — real Caterpillar values like
 *    "100-Series"/"300-Series" were silently absent, not just zero-counted, because the
 *    catalog has 100+ distinct Series names in total). This store has 500+ manufacturers and
 *    well over 100 series/model names, so every field could hit this via that path.
 * 2. Far more serious: `collection(handle:"all").products(filters:[...])` was confirmed to
 *    return **wrong results**, not just capped facets. A product provably in the "all"
 *    collection with `custom.manufacturer` provably equal to "CATERPILLAR" was excluded from
 *    a `filters: [{productMetafield: manufacturer="CATERPILLAR"}]` query against that same
 *    collection — of 1,034 real Caterpillar products, only 18 were findable that way. This is
 *    a genuine Shopify-side index bug on the collection-scoped filtering path specifically.
 *    `search(query: "", types: PRODUCT, productFilters: [...])` was verified against the same
 *    data and returns correct, complete results (the same Caterpillar filter found ~1000+
 *    real matches) — so every query below uses `search`, not `collection.products`, and
 *    doesn't depend on any particular collection existing at all. `search` also exposes a
 *    real `totalCount`, unlike collection product connections.
 *
 * Every field's combobox options are built from real product data, never from `Filter.values`.
 * Manufacturer (index 0, unscoped — could mean scanning the whole 50k+ catalog) uses a
 * precomputed static list, assets/manufacturer-list.json (see scripts/rebuild-manufacturer-
 * list.py and CLAUDE.md for the full story — that script still paginates
 * collection.products directly, since it only needs to read metafields, not filter by them).
 * Every other field is scoped by the filters already chosen (e.g. Series only needs the
 * selected Manufacturer's own products), fetched live by paginating `search(...)` and reading
 * each product's own metafield (fetchFieldOptions()). Either way, each resulting option's
 * `input` is hand-built in the same `{"productMetafield":{"namespace":"custom","key":...,
 * "value":...}}` shape Shopify's own filter aggregation used to produce, so
 * handleChange()/getSelectedInput() need no special-casing — every field is just a Combobox,
 * however its options were populated.
 *
 * Optional redirect-instead-of-inline-results mode (data-redirect-url, wired through
 * snippets/parts-finder-form.liquid's redirect_url param): when set, Search encodes the chosen
 * filters as URL query params (buildResultUrl()) and navigates there instead of rendering
 * results in place. Meant for popup/mega-menu instances that should hand off to the dedicated
 * Parts Finder page. Any instance *without* its own redirectUrl treats itself as the receiving
 * end — applyFiltersFromUrl() reads those same params back out on load, walks the fields in
 * order pre-selecting/cascading each one (mirroring handleChange(), but awaited sequentially
 * instead of event-driven), and searches automatically once done.
 */
(() => {
  const API_VERSION = '2024-01';
  // Shows the "(N)" match count after each dropdown option's label — set false to hide it again.
  const SHOW_OPTION_COUNTS = true;
  // Fixed field order/keys, matching snippets/parts-finder-form.liquid's own hardcoded list —
  // also each field's custom.* metafield key.
  const FIELD_KEYS = ['manufacturer', 'series', 'model', 'engine', 'year', 'serial_number'];
  const MANUFACTURER_INDEX = 0;

  // Confirms the token is actually valid at load time, surfacing a misconfiguration early
  // rather than waiting until the shopper interacts with a field.
  const SHOP_CHECK_QUERY = `
    query PartsFinderShopCheck {
      shop { id }
    }
  `;

  // Paginated so it can read every matching product's metafield directly — see the class-level
  // comment for why this uses search() instead of collection.products(). $key is one of
  // FIELD_KEYS.
  const FIELD_VALUES_QUERY = `
    query PartsFinderFieldValues($filters: [ProductFilter!], $key: String!, $cursor: String) {
      search(query: "", types: PRODUCT, first: 250, productFilters: $filters, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          ... on Product {
            metafield(namespace: "custom", key: $key) { value }
          }
        }
      }
    }
  `;

  const SEARCH_QUERY = `
    query PartsFinderSearch($filters: [ProductFilter!], $first: Int!, $cursor: String) {
      search(query: "", types: PRODUCT, first: $first, productFilters: $filters, after: $cursor) {
        totalCount
        pageInfo { hasNextPage endCursor }
        nodes {
          ... on Product {
            title
            handle
            productType
            featuredImage { url }
            priceRange { minVariantPrice { amount currencyCode } }
            variants(first: 1) {
              nodes { id availableForSale }
            }
          }
        }
      }
    }
  `;

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[char]));
  }

  // A searchable dropdown: click/keyboard opens a panel with a text input pinned at the top
  // and a filtered option list below it, matching the reference UI the client asked for
  // (search box built into the open dropdown itself) — not achievable with a native <select>
  // or a plain <input list="..."> (tried both; neither actually renders this way). Each
  // instance owns one field's markup (.parts-finder__combobox in snippets/parts-finder-
  // form.liquid): a trigger <button> showing the current value, and a hidden-until-open panel
  // with the search <input> and the option <ul>.
  //
  // Options are always the full in-memory {label, count, input} objects (see
  // PartsFinderStorefront.populateFieldOptions/loadManufacturerList) — no round-tripping
  // through DOM dataset strings the way a real <select>'s <option> would need.
  class Combobox {
    constructor(root, { onChange }) {
      this.root = root;
      this.trigger = root.querySelector('.parts-finder__combobox-trigger');
      this.valueLabel = root.querySelector('.parts-finder__combobox-value');
      this.panel = root.querySelector('.parts-finder__combobox-panel');
      this.searchInput = root.querySelector('.parts-finder__combobox-search');
      this.list = root.querySelector('.parts-finder__combobox-list');
      this.placeholder = this.valueLabel.textContent.trim();
      this.onChange = onChange;
      this.onBeforeOpen = null; // set by PartsFinderStorefront to close sibling comboboxes
      this.options = [];
      this.filtered = [];
      this.selectedOption = null;
      this.emptyLabel = null;
      this.activeIndex = -1;

      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.open();
        }
      });
      this.searchInput.addEventListener('input', () => {
        this.activeIndex = -1;
        this.renderList();
      });
      this.searchInput.addEventListener('keydown', (event) => this.handleSearchKeydown(event));
      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) this.close();
      });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.close();
      });
      // The panel is `position: fixed` (see positionPanel()) so it can render outside any
      // clipping ancestor (e.g. the mega-menu popup's own scrolling modal) — it doesn't move
      // with the page the way an absolutely-positioned panel would, so close instead of
      // drifting out of alignment. Capture:true catches scrolling on any ancestor, not just the
      // window itself (needed for the modal's own internal scroll) — but that also means it
      // catches scrolling *inside the option list itself* (a real bug: it made the list
      // un-scrollable, closing itself the instant you tried), so it has to ignore scroll events
      // whose target is within this combobox's own panel.
      window.addEventListener(
        'scroll',
        (event) => {
          if (this.panel.contains(event.target)) return;
          this.close();
        },
        { capture: true }
      );
      window.addEventListener('resize', () => this.close());
    }

    // Positions the (position: fixed) panel directly under the trigger, viewport-relative —
    // see the class-level note on .parts-finder__combobox-panel for why fixed instead of
    // absolute.
    positionPanel() {
      const rect = this.trigger.getBoundingClientRect();
      this.panel.style.position = 'fixed';
      this.panel.style.top = `${rect.bottom + 4}px`;
      this.panel.style.left = `${rect.left}px`;
      this.panel.style.width = `${rect.width}px`;
    }

    // options: [{label, count, input}]. emptyLabel shows in the trigger when options is empty
    // (e.g. "N/A") instead of the field's normal "Select X" placeholder.
    setOptions(options, emptyLabel) {
      this.options = options;
      this.selectedOption = null;
      this.emptyLabel = emptyLabel || null;
      this.updateTrigger();
    }

    setDisabled(disabled) {
      this.trigger.disabled = disabled;
      if (disabled) this.close();
    }

    reset() {
      this.selectedOption = null;
      this.updateTrigger();
    }

    getSelectedOption() {
      return this.selectedOption;
    }

    isOpen() {
      return !this.panel.hidden;
    }

    open() {
      if (this.trigger.disabled || this.isOpen()) return;
      if (this.onBeforeOpen) this.onBeforeOpen();
      this.positionPanel();
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.searchInput.value = '';
      this.activeIndex = -1;
      this.renderList();
      this.searchInput.focus();
    }

    close() {
      if (!this.isOpen()) return;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    toggle() {
      if (this.isOpen()) this.close();
      else this.open();
    }

    renderList() {
      const query = this.searchInput.value.trim().toLowerCase();
      this.filtered = query
        ? this.options.filter((option) => option.label.toLowerCase().includes(query))
        : this.options;

      if (!this.filtered.length) {
        this.list.innerHTML = '<li class="parts-finder__combobox-empty">No matches</li>';
        return;
      }

      this.list.innerHTML = this.filtered
        .map((option, index) => {
          const selected = option === this.selectedOption;
          const label = SHOW_OPTION_COUNTS && typeof option.count === 'number'
            ? `${option.label} (${option.count})`
            : option.label;
          return `<li role="option" data-index="${index}" aria-selected="${selected}" class="parts-finder__combobox-option${selected ? ' is-selected' : ''}">${escapeHtml(label)}</li>`;
        })
        .join('');

      [...this.list.children].forEach((item) => {
        // mousedown (not click) fires before the search input's blur/focusout would close the
        // panel, so the selection still registers.
        item.addEventListener('mousedown', (event) => {
          event.preventDefault();
          const index = Number(item.dataset.index);
          if (!Number.isNaN(index)) this.select(this.filtered[index]);
        });
      });
    }

    select(option) {
      if (!option) return;
      this.selectedOption = option;
      this.updateTrigger();
      this.close();
      this.trigger.focus();
      this.onChange(option);
    }

    updateTrigger() {
      this.valueLabel.textContent = this.selectedOption?.label || this.emptyLabel || this.placeholder;
    }

    handleSearchKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.trigger.focus();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.select(this.filtered[this.activeIndex] || this.filtered[0]);
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.filtered.length - 1);
        this.highlightActive();
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.highlightActive();
      }
    }

    highlightActive() {
      [...this.list.children].forEach((item, index) => {
        item.classList.toggle('is-active', index === this.activeIndex);
      });
      const activeItem = this.list.children[this.activeIndex];
      if (activeItem) activeItem.scrollIntoView({ block: 'nearest' });
    }
  }

  class PartsFinderStorefront extends HTMLElement {
    connectedCallback() {
      this.shopDomain = this.dataset.shopDomain || location.hostname;
      this.storefrontToken = this.dataset.storefrontToken || '';
      this.designMode = this.dataset.designMode === 'true';
      this.resultLimit = Number(this.dataset.resultLimit) || 50;
      this.fieldLabels = JSON.parse(this.dataset.fieldLabels || '[]');
      this.manufacturerListUrl = this.dataset.manufacturerListUrl || '';
      this.manufacturerList = [];
      this.redirectUrl = this.dataset.redirectUrl || '';
      this.activeFilters = new Array(this.fieldLabels.length).fill(null);
      this.lastItems = [];
      this.activeTab = 'all';

      this.comboboxes = [...this.querySelectorAll('.parts-finder__combobox')].map((root, index) =>
        new Combobox(root, { onChange: () => this.handleChange(index) })
      );
      this.comboboxes.forEach((combobox, index) => {
        // Only one field's dropdown open at a time.
        combobox.onBeforeOpen = () => {
          this.comboboxes.forEach((other, otherIndex) => {
            if (otherIndex !== index) other.close();
          });
        };
      });
      this.clearButton = this.querySelector('.parts-finder__clear');
      this.searchButton = this.querySelector('.parts-finder__search');
      this.fieldsWrap = this.querySelector('.parts-finder__fields');
      this.filtersToggle = this.querySelector('.parts-finder__filters-toggle');
      this.filtersToggleLabel = this.querySelector('.parts-finder__filters-toggle-label');
      // Popup/mega-menu instances render with show_filters_toggle: false (no button in the
      // DOM) — only the dedicated page shows it.
      if (this.filtersToggle) {
        this.filtersToggle.addEventListener('click', () => this.setFiltersVisible(Boolean(this.fieldsWrap.hidden)));
      }
      this.statusText = this.querySelector('.parts-finder__status-text');
      this.statusSpinner = this.querySelector('.parts-finder__status-spinner');
      this.resultsWrapper = this.querySelector('.parts-finder__results');
      this.grid = this.querySelector('.parts-finder__grid');
      this.count = this.querySelector('.parts-finder__count');
      this.loadMoreButton = this.querySelector('.parts-finder__load-more');
      this.tabs = [...this.querySelectorAll('.parts-finder__tab')];
      this.layoutButtons = [...this.querySelectorAll('.parts-finder__layout-switch-button')];

      // applyFiltersFromUrl() only makes sense on a *receiving* instance (no redirect_url of
      // its own — i.e. not a popup configured to hand off elsewhere) — reads back the filters
      // a redirecting instance encoded into the URL, pre-selects them, and searches
      // automatically. See the class-level comment on redirectUrl/buildResultUrl.
      //
      // Hide the filter row immediately here — synchronously, before any of that async
      // prefill/search work even starts — rather than waiting for it to finish and having the
      // filters visibly flash on screen first (that work involves several network round trips:
      // the manufacturer list fetch, one cascade fetch per URL-supplied field, then the search
      // itself). Re-shown in applyFiltersFromUrl() if the prefill doesn't actually pan out.
      const hasUrlFilters = !this.redirectUrl && Boolean(new URLSearchParams(location.search).get(FIELD_KEYS[MANUFACTURER_INDEX]));
      if (hasUrlFilters) this.setFiltersVisible(false);

      const manufacturerListLoaded = this.loadManufacturerList();
      if (!this.redirectUrl) manufacturerListLoaded.then(() => this.applyFiltersFromUrl());
      this.clearButton.addEventListener('click', () => this.reset());
      this.searchButton.addEventListener('click', () => this.search());
      this.loadMoreButton.addEventListener('click', () => this.loadMore());
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.setActiveTab(tab.dataset.tab)));
      // Cards are rebuilt via innerHTML on every render, so their Add to cart buttons are
      // wired via delegation on the grid itself rather than re-attaching listeners each time.
      this.grid.addEventListener('click', (event) => {
        const button = event.target.closest('.parts-finder__card-add');
        if (button) this.addToCart(button.dataset.variantId, button);
      });
      this.layoutButtons.forEach((button) => button.addEventListener('click', () => this.setLayout(button.dataset.layout)));
      // Own storage key (not the theme's "product-layout") since this is a distinct grid.
      this.setLayout(this.getStoredLayout() || 'grid');
      this.wireUpModalOutsideClickClose();

      if (!this.storefrontToken) {
        this.setStatus('', 'Parts Finder is not connected to the Storefront API yet.');
        return;
      }

      this.loadInitialFilters();
    }

    // When this instance is rendered inside a <modal-dialog> (the mega-menu "Find by Machine"
    // popup — see blocks/ai_gen_block_11addba.liquid and snippets/header-main-menu-megamenu.
    // liquid), that popup is styled to look like a mega-menu dropdown, not a blocking modal —
    // its backdrop is set to `pointer-events: none` in CSS so the rest of the page (nav
    // included) stays clickable while it's open. That CSS has no way to also *close* the popup
    // when you click elsewhere, though, since the (now click-through) backdrop can no longer
    // catch that click itself — this replaces it: any click outside the popup's actual visible
    // panel closes it, the same way clicking outside a normal dropdown would.
    wireUpModalOutsideClickClose() {
      const modal = this.closest('modal-dialog');
      if (!modal) return;
      document.addEventListener('click', (event) => {
        if (this.suppressNextOutsideClick) {
          this.suppressNextOutsideClick = false;
          return;
        }
        if (!modal.hasAttribute('open')) return;
        if (event.target.closest('modal-opener')) return; // the trigger that opens it
        const panel = modal.querySelector('.modal-dialog');
        if (panel && !panel.contains(event.target)) modal.hide();
      });
    }

    async graphql(query, variables) {
      const endpoint = `https://${this.shopDomain}/api/${API_VERSION}/graphql.json`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({ query, variables }),
      });
      const payload = await response.json();
      if (payload.errors) {
        // GraphQL responses can carry partial per-field errors *alongside* otherwise-usable
        // data (e.g. one field on a handful of nodes) — that's expected, not fatal. Log them
        // readably (not a collapsed object array) and only actually fail the request if no
        // usable data came back with it.
        const messages = payload.errors.map((error) => error.message).join(' | ');
        console.error(`Parts Finder Storefront API error (${payload.errors.length}): ${messages}`);
      }
      if (!response.ok || !payload.data) {
        throw new Error(payload.errors?.[0]?.message || `Storefront API error (${response.status})`);
      }
      return payload.data;
    }

    activeFiltersUpTo(index) {
      return this.activeFilters.slice(0, index).filter(Boolean);
    }

    // Confirms the Storefront token is actually valid at load time, surfacing a
    // misconfiguration early rather than waiting until the shopper interacts with a field.
    async loadInitialFilters() {
      this.setLoading(true);
      const requestId = this.nextRequest();
      try {
        const data = await this.graphql(SHOP_CHECK_QUERY, {});
        if (!this.isCurrentRequest(requestId)) return;
        if (!data.shop) {
          this.setStatus(
            'This tool is temporarily unavailable. Please try again later.',
            'Storefront API responded with no shop — check the access token.'
          );
          return;
        }
        this.setStatus('');
      } catch (error) {
        if (!this.isCurrentRequest(requestId)) return;
        this.setStatus(
          'This tool is temporarily unavailable. Please try again later.',
          'Could not reach the Storefront API. Check the access token.'
        );
      } finally {
        if (this.isCurrentRequest(requestId)) this.setLoading(false);
      }
    }

    // Fetches the full manufacturer list (assets/manufacturer-list.json) once and populates
    // the Manufacturer combobox directly from it — independent of the Storefront API
    // token/collection (it's a plain theme asset, not a GraphQL query) and of Shopify's filter
    // aggregation, which is what's capped at 100 values in the first place (see the class-level
    // comment). Each option's `input` is hand-built in the exact shape Shopify's own filter
    // aggregation uses, so handleChange()/getSelectedInput() need no special-casing for this
    // field — it's a combobox populated from a different source, same as every other field.
    async loadManufacturerList() {
      const combobox = this.comboboxes[MANUFACTURER_INDEX];
      if (!combobox || !this.manufacturerListUrl) return;
      try {
        const response = await fetch(this.manufacturerListUrl);
        const list = await response.json();
        this.manufacturerList = Array.isArray(list) ? list : [];
      } catch (error) {
        console.error('Parts Finder: could not load manufacturer list', error);
        this.manufacturerList = [];
      }
      combobox.setOptions(
        this.manufacturerList.map((name) => ({
          label: name,
          input: { productMetafield: { namespace: 'custom', key: 'manufacturer', value: name } },
        }))
      );
    }

    // Pages through every product matching `filters` and tallies the distinct values of one
    // metafield (fieldKey) directly — see the class-level comment for why this replaces asking
    // Shopify to aggregate filter values itself. Scoped by whatever's already selected (e.g.
    // Series only scans the chosen Manufacturer's own products), so this is normally 1-2 pages.
    // Returns { options, total } — total is every matching product scanned, regardless of
    // whether it had a value for this metafield (used for "All <field>"'s own count, since
    // "All" means every product matching the filters so far, not just the ones with a value).
    async fetchFieldOptions(fieldKey, filters, requestId) {
      const counts = new Map(); // exact label -> count
      let total = 0;
      let cursor = null;
      for (;;) {
        const data = await this.graphql(FIELD_VALUES_QUERY, { filters, key: fieldKey, cursor });
        if (!this.isCurrentRequest(requestId)) return { options: [], total: 0 };
        const results = data.search;
        for (const node of results?.nodes || []) {
          total += 1;
          const value = node.metafield?.value?.trim();
          if (!value || /^n\/?a$/i.test(value)) continue;
          counts.set(value, (counts.get(value) || 0) + 1);
        }
        if (!results?.pageInfo?.hasNextPage) break;
        cursor = results.pageInfo.endCursor;
      }
      const options = [...counts.entries()]
        .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
        .map(([label, count]) => ({ label, count }));
      return { options, total };
    }

    // Populates comboboxes[index] from `options` (see fetchFieldOptions). "N/A" — e.g. this
    // machine has no recorded Year — contributes nothing new to narrow by, so we automatically
    // chain forward to the next field using the same filters, rather than leaving the cascade
    // stuck on a field nobody can select.
    populateFieldOptions(index, options, total) {
      const combobox = this.comboboxes[index];
      const fieldKey = FIELD_KEYS[index];
      const mapped = options.map(({ label, count }) => ({
        label,
        count,
        input: { productMetafield: { namespace: 'custom', key: fieldKey, value: label } },
      }));
      // "All <field>" — lets a shopper explicitly skip narrowing by this field (Manufacturer
      // excluded; it's the one required field) instead of only ever being able to narrow
      // forward. Its `input` is null, same as never having selected anything for this field,
      // so activeFiltersUpTo()'s `.filter(Boolean)` already drops it from later queries with no
      // extra handling needed — see handleChange()'s cascade-trigger condition for the one place
      // that does need to treat "explicitly chose All" differently from "never touched". Its
      // count is the total scanned (see fetchFieldOptions), not a sum of the other options'
      // counts, since some products can have no value at all for this field and still match.
      if (options.length && index !== MANUFACTURER_INDEX) {
        mapped.unshift({ label: `All ${this.fieldLabels[index]}`, count: total, input: null });
      }
      combobox.setOptions(mapped, options.length ? null : 'N/A');
      combobox.setDisabled(options.length === 0);
      this.setStatus('');
    }

    async cascadeFrom(index, filters, requestId) {
      const { options, total } = await this.fetchFieldOptions(FIELD_KEYS[index], filters, requestId);
      if (!this.isCurrentRequest(requestId)) return;
      this.populateFieldOptions(index, options, total);
      if (options.length === 0 && index < this.comboboxes.length - 1) {
        await this.cascadeFrom(index + 1, filters, requestId);
      }
    }

    // Reads whatever's currently selected in comboboxes[index] and returns the Storefront
    // filter `input` object for it (or null if nothing's selected) — same for every field,
    // including Manufacturer, whose options are populated by loadManufacturerList() with a
    // hand-built `input` rather than one read off Shopify's filter aggregation (see the
    // class-level comment), but in the identical shape either way.
    getSelectedInput(index) {
      return this.comboboxes[index].getSelectedOption()?.input || null;
    }

    async handleChange(index) {
      // A combobox option's mousedown handler hides its panel synchronously (see Combobox.select()),
      // before this runs. The browser still fires a follow-up `click` on mouseup, but since the
      // option is already hidden by then, hit-testing at that point can't land on it — if the
      // option was rendered in the panel's overflow area (past the popup's own short box,
      // position:fixed lets it float over real page content behind), the click resolves to
      // whatever page element is actually there instead, which wireUpModalOutsideClickClose()
      // then sees as a click outside the popup and closes it. Suppressing exactly the next
      // document click here (set synchronously during mousedown, well before that click fires)
      // fixes it regardless of what element the click actually lands on.
      this.suppressNextOutsideClick = true;
      this.activeFilters[index] = this.getSelectedInput(index);
      // Distinct from activeFilters[index]: "All <field>" is a real, explicit choice whose
      // `input` is null (same effect as never selecting anything for this field), but it should
      // still cascade the *next* field forward — unlike a field nobody has touched yet.
      const hasSelection = Boolean(this.comboboxes[index].getSelectedOption());

      for (let i = index + 1; i < this.comboboxes.length; i += 1) {
        this.activeFilters[i] = null;
        this.comboboxes[i].setDisabled(true);
        this.comboboxes[i].reset();
      }

      this.searchButton.disabled = !this.activeFilters[0];
      this.resultsWrapper.hidden = true;
      this.setStatus('');

      if (hasSelection && index < this.comboboxes.length - 1) {
        this.setStatus('Loading…');
        this.setLoading(true);
        // Guard against out-of-order responses: if the user changes a dropdown again,
        // clears, or searches before this request resolves, its result is discarded.
        const requestId = this.nextRequest();
        try {
          await this.cascadeFrom(index + 1, this.activeFiltersUpTo(index + 1), requestId);
        } catch (error) {
          if (!this.isCurrentRequest(requestId)) return;
          this.setStatus('Could not load the next set of options. Please try again.', error.message);
        } finally {
          if (this.isCurrentRequest(requestId)) this.setLoading(false);
        }
      }
    }

    nextRequest() {
      this.requestSeq = (this.requestSeq || 0) + 1;
      return this.requestSeq;
    }

    isCurrentRequest(requestId) {
      return requestId === this.requestSeq;
    }

    reset() {
      this.nextRequest(); // invalidate any in-flight handleChange/search response
      this.activeFilters = new Array(this.fieldLabels.length).fill(null);
      this.comboboxes.forEach((combobox, index) => {
        combobox.reset();
        if (index !== MANUFACTURER_INDEX) combobox.setDisabled(true);
      });
      this.searchButton.disabled = true;
      this.resultsWrapper.hidden = true;
      this.setStatus('');
      this.setFiltersVisible(true);
    }

    // Builds an absolute URL to redirectUrl with the currently selected filters encoded as
    // query params — one per FIELD_KEYS entry, value = the exact option label chosen. Skips
    // unselected fields and "All <field>" (its input is null, same as unselected — nothing
    // meaningful to encode either way).
    buildResultUrl() {
      const params = new URLSearchParams();
      this.comboboxes.forEach((combobox, index) => {
        const option = combobox.getSelectedOption();
        if (option && option.input) params.set(FIELD_KEYS[index], option.label);
      });
      const url = new URL(this.redirectUrl, location.origin);
      url.search = params.toString();
      return url.toString();
    }

    // Reads filters back out of the URL (see buildResultUrl) on a receiving instance, walking
    // FIELD_KEYS in order the same way a shopper manually filling the form would — pre-selects
    // each field, awaits its cascade before moving to the next (mirroring handleChange(), but
    // sequential/awaited instead of event-driven), and searches automatically once done. Stops
    // at the first field with no URL value or no matching option, leaving the rest as "not yet
    // selected" exactly like a partially-filled manual search would.
    async applyFiltersFromUrl() {
      const params = new URLSearchParams(location.search);
      let appliedAny = false;
      for (let index = 0; index < this.comboboxes.length; index += 1) {
        const value = params.get(FIELD_KEYS[index]);
        if (!value) break;
        const combobox = this.comboboxes[index];
        const option = combobox.options.find((candidate) => candidate.label.toLowerCase() === value.toLowerCase());
        if (!option) break;

        combobox.selectedOption = option;
        combobox.updateTrigger();
        this.activeFilters[index] = option.input;
        appliedAny = true;

        if (index < this.comboboxes.length - 1) {
          const requestId = this.nextRequest();
          this.setStatus('Loading…');
          this.setLoading(true);
          try {
            await this.cascadeFrom(index + 1, this.activeFiltersUpTo(index + 1), requestId);
          } catch (error) {
            break;
          } finally {
            if (this.isCurrentRequest(requestId)) this.setLoading(false);
          }
        }
      }

      this.searchButton.disabled = !this.activeFilters[0];
      if (appliedAny && this.activeFilters[0]) await this.search();
      // Filters were already hidden synchronously up front (connectedCallback), before any of
      // this ran, on the assumption it would pan out — if it didn't (no matching manufacturer
      // in the URL, or the search itself failed), bring them back rather than leaving the
      // shopper stuck looking at a blank, collapsed page with nothing to interact with.
      if (this.resultsWrapper.hidden) this.setFiltersVisible(true);
    }

    async search() {
      if (!this.activeFilters[0]) return;
      if (this.redirectUrl) {
        location.href = this.buildResultUrl();
        return;
      }
      this.searchFilters = this.activeFilters.filter(Boolean);
      this.lastItems = [];
      this.searchCursor = null;
      this.hasMoreResults = false;
      this.totalResultCount = null;
      this.setStatus('Searching…');
      this.searchButton.disabled = true;
      this.searchButton.classList.add('loading');
      const requestId = this.nextRequest();
      try {
        await this.fetchResultsPage(requestId);
        if (!this.isCurrentRequest(requestId)) return;
        this.resultsWrapper.hidden = false;
        this.setActiveTab('all');
      } catch (error) {
        if (!this.isCurrentRequest(requestId)) return;
        this.setStatus('Search failed. Please try again.', error.message);
        this.resultsWrapper.hidden = true;
      } finally {
        this.searchButton.classList.remove('loading');
        if (this.isCurrentRequest(requestId)) this.searchButton.disabled = !this.activeFilters[0];
      }
    }

    async loadMore() {
      if (!this.hasMoreResults) return;
      this.loadMoreButton.disabled = true;
      this.loadMoreButton.classList.add('loading');
      const requestId = this.nextRequest();
      try {
        await this.fetchResultsPage(requestId);
      } catch (error) {
        if (!this.isCurrentRequest(requestId)) return;
        this.setStatus('Could not load more results. Please try again.', error.message);
      } finally {
        this.loadMoreButton.classList.remove('loading');
        if (this.isCurrentRequest(requestId)) this.loadMoreButton.disabled = false;
      }
    }

    // Fetches one page of results (using this.searchCursor) and appends it to this.lastItems.
    // Shared by search() (first page) and loadMore() (every page after). search() (unlike
    // collection.products) exposes a real totalCount, so renderResults() can show an honest
    // "N of M" instead of just "N loaded".
    async fetchResultsPage(requestId) {
      const data = await this.graphql(SEARCH_QUERY, {
        filters: this.searchFilters,
        first: this.resultLimit,
        cursor: this.searchCursor,
      });
      if (!this.isCurrentRequest(requestId)) return;
      const results = data.search;
      this.lastItems = this.lastItems.concat(results?.nodes || []);
      this.hasMoreResults = Boolean(results?.pageInfo?.hasNextPage);
      this.searchCursor = results?.pageInfo?.endCursor || null;
      this.totalResultCount = typeof results?.totalCount === 'number' ? results.totalCount : null;
      this.setStatus('');
      this.renderResults();
    }

    setActiveTab(tab) {
      this.activeTab = tab;
      this.tabs.forEach((button) => button.setAttribute('aria-selected', String(button.dataset.tab === tab)));
      this.renderResults();
    }

    // Shows/hides the Manufacturer..Serial Number fields row, independent of the results
    // themselves. Defaults visible; applyFiltersFromUrl() collapses it automatically after a
    // successful redirect-with-preapplied-filters arrival (arriving already filtered — the
    // filter row is just clutter above the results until the shopper asks for it), and the
    // toggle button lets a shopper bring it back to adjust their selection either way.
    setFiltersVisible(visible) {
      this.fieldsWrap.hidden = !visible;
      if (this.filtersToggle) this.filtersToggle.setAttribute('aria-expanded', String(visible));
      if (this.filtersToggleLabel) this.filtersToggleLabel.textContent = visible ? 'Hide filters' : 'Show filters';
    }

    getStoredLayout() {
      try {
        return localStorage.getItem('parts-finder-layout');
      } catch (error) {
        return null;
      }
    }

    setLayout(layout) {
      this.layout = layout;
      this.layoutButtons.forEach((button) => {
        const isActive = button.dataset.layout === layout;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
      this.grid.classList.toggle('parts-finder__grid--row', layout === 'row');
      this.grid.classList.toggle('parts-finder__grid--list', layout === 'list');
      try {
        localStorage.setItem('parts-finder-layout', layout);
      } catch (error) {
        // Private browsing / blocked storage — layout choice just won't persist across visits.
      }
    }

    // Uses Shopify's native Product Type field ("Filter Service Kit" vs "Air Filter",
    // "Oil Filter", etc.) rather than the custom.kit_type metafield — that metafield's
    // values turned out to be free-text descriptions of what's included (e.g. "Air/Oil/Fuel"),
    // not a consistent "is this a kit" flag, so matching "kit" in it missed real kits.
    kindOf(item) {
      return /kit/i.test(item.productType || '') ? 'kit' : 'filter';
    }

    renderResults() {
      const items = this.activeTab === 'all' ? this.lastItems : this.lastItems.filter((item) => this.kindOf(item) === this.activeTab);

      // search() exposes a real totalCount, but it counts across *all* tabs combined — only
      // meaningful to show while the "Show All" tab is active. The Filters/Filter Kits tabs
      // split client-side (kindOf()), so there's no true total for just that subset; stays
      // honest ("N loaded") there instead of implying a total we don't actually have.
      if (this.activeTab === 'all' && typeof this.totalResultCount === 'number') {
        this.count.textContent = `${items.length} of ${this.totalResultCount} result${this.totalResultCount === 1 ? '' : 's'}`;
      } else {
        this.count.textContent = `${items.length} result${items.length === 1 ? '' : 's'} loaded`;
      }
      // hasMoreResults reflects whether the raw search (across all tabs) has another page —
      // but the active tab (Filters/Filter Kits) can have zero visible items even while that's
      // true, since the split happens client-side from whatever's already loaded. Showing
      // "Load more" next to "No matching parts found" reads as broken, so it also requires the
      // current tab to actually have items on screen.
      this.loadMoreButton.hidden = !this.hasMoreResults || !items.length;

      if (!items.length) {
        this.grid.innerHTML = '<li class="parts-finder__empty">No matching parts found.</li>';
        return;
      }

      this.grid.innerHTML = items.map((item) => this.renderCard(item)).join('');
    }

    renderCard(item) {
      const href = `/products/${this.escape(item.handle)}`;
      const image = item.featuredImage?.url;
      const media = image
        ? `<img class="parts-finder__card-image" src="${this.escape(image)}" alt="" loading="lazy">`
        : '<span class="parts-finder__card-image parts-finder__card-image--placeholder"></span>';
      const title = this.escape(item.title);
      const price = this.formatPrice(item.priceRange?.minVariantPrice);
      const variant = item.variants?.nodes?.[0];
      const variantId = variant?.id ? variant.id.split('/').pop() : null;
      const addToCartButton = variantId
        ? `
          <button
            type="button"
            class="button parts-finder__card-add"
            data-variant-id="${this.escape(variantId)}"
            ${variant.availableForSale ? '' : 'disabled'}
          >
            ${variant.availableForSale ? 'Add to cart' : 'Out of stock'}
            <div class="button-overlay-spinner hidden">
              <span class="spinner-xs"></span>
            </div>
          </button>
        `
        : '';

      return `
        <li class="parts-finder__card">
          <div class="parts-finder__card-media">${media}</div>
          <div class="parts-finder__card-body">
            <p class="parts-finder__card-title"><a href="${href}">${title}</a></p>
            <div class="parts-finder__card-actions">
              ${price ? `<p class="parts-finder__card-price">${price}</p>` : ''}
              ${addToCartButton}
            </div>
          </div>
        </li>
      `;
    }

    formatPrice(minVariantPrice) {
      const amount = minVariantPrice?.amount;
      const currency = minVariantPrice?.currencyCode;
      if (!amount || !currency) return '';
      try {
        return `${new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)} ${currency}`;
      } catch (error) {
        return `${amount} ${currency}`;
      }
    }

    // Mirrors the theme's own add-to-cart flow (assets/product-form.js): POST to
    // routes.cart_add_url, then hand the response to the page's existing <mini-cart>
    // component so it updates/opens exactly like every other add-to-cart button on the
    // site, instead of building a separate one-off cart UI.
    async addToCart(variantId, button) {
      if (!variantId || button.classList.contains('loading')) return;
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');

      const minicart = document.querySelector('mini-cart');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (minicart) {
        formData.append('sections', minicart.getSectionsToRender().map((section) => section.section));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json();
        if (data.status) {
          throw new Error(data.description || data.message || 'Could not add to cart');
        }

        if (minicart) {
          minicart.renderContents(data);
          minicart.open(button);
        } else if (window.themeSettings?.redirectToCart) {
          location.href = window.routes.cart_url;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartItemAdd, { source: 'parts-finder', items: [data] });
        }
      } catch (error) {
        console.error('Parts Finder: add to cart failed', error);
        this.setStatus('Could not add to cart. Please try again.');
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      }
    }

    escape(value) {
      return escapeHtml(value);
    }

    // `debugMessage` (if given) is shown instead of `message` only in the theme editor's
    // design mode — real shoppers never see internal Search & Discovery/config diagnostics,
    // only a friendly generic message (or nothing, for the plain operational statuses that
    // pass no `message` at all, like "Loading…").
    setStatus(message, debugMessage) {
      this.statusText.textContent = this.designMode && debugMessage ? debugMessage : message;
    }

    setLoading(isLoading) {
      this.statusSpinner.classList.toggle('hidden', !isLoading);
    }
  }

  if (!customElements.get('parts-finder-storefront')) {
    customElements.define('parts-finder-storefront', PartsFinderStorefront);
  }
})();
